import { PropsWithChildren } from 'react';
import { Button, Grid2, Stack, Typography, useTheme } from '@mui/material';
import SmsIcon from '@mui/icons-material/SmsOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import AddIcon from '@mui/icons-material/AddSharp';
import AutomationBadge from '../AutomationBadge';
import {
  AutomationListItem,
  AutomationRecipe,
} from '../../../types/marketing/automations';

function CardShell({ children }: PropsWithChildren) {
  const theme = useTheme();
  return (
    <Grid2
      size={1}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      gap={2}
      p={2.5}
      minHeight="220px"
      boxSizing="border-box"
      border={`1px solid ${theme.palette.darkerLightGrey.main}`}
      borderRadius="10px"
      bgcolor="background.paper">
      {children}
    </Grid2>
  );
}

interface RecipeCardProps {
  recipe: AutomationRecipe;
  summary: string;
  existingAutomation: AutomationListItem | null;
  isSetUpDisabled: boolean;
  onSetUp: () => void;
  onViewExisting: () => void;
}

export function AutomationRecipeCard({
  recipe,
  summary,
  existingAutomation,
  isSetUpDisabled,
  onSetUp,
  onViewExisting,
}: RecipeCardProps) {
  const ChannelIcon = recipe.channel === 'EMAIL' ? EmailIcon : SmsIcon;

  return (
    <CardShell>
      <Stack gap={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}>
          <ChannelIcon color="action" fontSize="small" />
          {existingAutomation && (
            <AutomationBadge status={existingAutomation.status} />
          )}
        </Stack>
        <Typography variant="h5">{recipe.title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {recipe.description}
        </Typography>
        <Typography variant="caption" fontWeight={600}>
          {summary}
        </Typography>
      </Stack>
      {existingAutomation ? (
        <Button variant="light" onClick={onViewExisting}>
          View
        </Button>
      ) : (
        <Button
          variant="base"
          color="primary"
          onClick={onSetUp}
          disabled={isSetUpDisabled}>
          Set up
        </Button>
      )}
    </CardShell>
  );
}

interface BuildYourOwnCardProps {
  onClick: () => void;
}

export function BuildYourOwnCard({ onClick }: BuildYourOwnCardProps) {
  return (
    <CardShell>
      <Stack gap={1}>
        <AddIcon color="action" fontSize="small" />
        <Typography variant="h5">Build your own</Typography>
        <Typography variant="body2" color="text.secondary">
          Pick a trigger, a delay and a message from scratch.
        </Typography>
      </Stack>
      <Button variant="light" onClick={onClick}>
        Start from scratch
      </Button>
    </CardShell>
  );
}
